import { ErrorPage } from '@/components/ErrorPage';
import { TablePageLayout } from '@/layouts/TablePageLayout';
import { useAuditLogs } from '@/queries/audit-logs';
import { formatRelativeTime } from '@/utils/format';
import ApiOutlined from '@ant-design/icons/ApiOutlined';
import FileSearchOutlined from '@ant-design/icons/FileSearchOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import Popover from 'antd/es/popover';
import Space from 'antd/es/space';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { DataTable, useDataTableState } from 'ui-components';
import type { DataTableColumnType } from 'ui-components';

import type { AuditLog, AuditLogFilters } from '@mai/types';

type AuditLogRow = AuditLog;

const ResourceIdText = styled.span`
  ${({ theme }) => `
  font-family: ${theme.fontFamilyCode};
  font-size: ${theme.fontSize}px;
`}
`;

const MetadataRow = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  padding: ${theme.paddingXXS}px 0;
  border-bottom: ${`${theme.lineWidth}px solid ${theme.colorBorderSecondary}`};
  
  &:last-child {
    border-bottom: none;
  }
`}
`;

const MetadataLabel = styled.span`
  ${({ theme }) => `
  color: ${theme.colorTextSecondary};
  font-weight: ${theme.fontWeightStrong};
`}
`;

const MetadataValue = styled.span`
  ${({ theme }) => `
  color: ${theme.colorText};
  font-family: ${theme.fontFamilyCode};
  font-size: ${theme.fontSize}px;
`}
`;

const PopoverContent = styled.div`
  ${({ theme }) => `
  max-width: ${theme.controlHeightLG * 10}px;
`}
`;

const PopoverTitle = styled.h4<{ $isFirst?: boolean }>`
  ${({ theme, $isFirst }) => `
  margin-top: ${$isFirst ? 0 : theme.size * 2}px;
  margin-bottom: ${theme.size}px;
`}
`;

const ChangesPre = styled.pre`
  ${({ theme }) => `
  margin: 0;
  font-size: ${theme.fontSizeSM}px;
`}
`;

const DetailIcon = styled(InfoCircleOutlined)`
  cursor: pointer;
`;

const EmptyStateIcon = styled(FileSearchOutlined)`
  ${({ theme }) => `
  font-size: ${theme.sizeXXL}px;
`}
`;

const actionColors: Record<string, string> = {
  create: 'green',
  delete: 'red',
  update: 'orange',
  read: 'blue',
  execute: 'purple',
  export: 'cyan',
  import: 'magenta',
};

const resourceTypeIcons: Record<string, React.ReactNode> = {
  team: <UserOutlined />,
  user: <UserOutlined />,
  apiToken: <ApiOutlined />,
};

const AuditLogsPage = () => {
  const { teamId } = useParams<{ teamId: string }>();

  const { requestState } = useDataTableState({
    filterMap: {
      action: 'action',
      userId: 'userId',
      resourceType: 'resourceType',
      eventType: 'eventType',
      dateRange: (value) => {
        const [startDate, endDate] = value.split(',');
        return { startDate, endDate };
      },
    },
  });

  const {
    data: auditLogsResponse,
    isLoading: logsLoading,
    error: logsError,
  } = useAuditLogs(
    teamId || null,
    requestState as Omit<AuditLogFilters, 'teamId'>,
  );

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    if (!auditLogsResponse?.items) {
      return {
        actions: [],
        users: [],
        resources: [],
        eventTypes: [],
      };
    }

    const auditLogs = auditLogsResponse.items;
    const actions = [...new Set(auditLogs.map((log) => log.action))];
    const userIds = [
      ...new Set(auditLogs.map((log) => log.userId).filter(Boolean)),
    ];
    const resources = [...new Set(auditLogs.map((log) => log.resourceType))];
    const eventTypes = [...new Set(auditLogs.map((log) => log.eventType))];

    return {
      actions,
      users: userIds as string[],
      resources,
      eventTypes,
    };
  }, [auditLogsResponse]);

  const columns: DataTableColumnType<AuditLogRow>[] = useMemo(
    () => [
      {
        title: 'Timestamp',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        render: (date: Date) => formatRelativeTime(date),
        sorter: true,
      },
      {
        title: 'Event',
        dataIndex: 'eventType',
        key: 'eventType',
        width: 200,
        ellipsis: true,
        sorter: true,
        filter: {
          type: 'select',
          placeholder: 'All Events',
          options: filterOptions.eventTypes.map((eventType) => ({
            label: eventType,
            value: eventType,
          })),
        },
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        width: 100,
        render: (action: string) => (
          <Tag color={actionColors[action] || 'default'}>
            {action.toUpperCase()}
          </Tag>
        ),
        sorter: true,
        filter: {
          type: 'select',
          placeholder: 'All Actions',
          options: filterOptions.actions.map((action) => ({
            label: action.toUpperCase(),
            value: action,
          })),
        },
      },
      {
        title: 'User',
        dataIndex: 'userId',
        key: 'userId',
        width: 200,
        render: (userId: string | null, record: AuditLogRow) => {
          if (!userId) return '-';
          if (record.apiTokenId) {
            return (
              <Space size="small">
                <ApiOutlined />
                <Tooltip title="API Token">
                  <ResourceIdText>{record.apiTokenId}</ResourceIdText>
                </Tooltip>
              </Space>
            );
          }
          return (
            <Space size="small">
              <UserOutlined />
              <ResourceIdText>{userId}</ResourceIdText>
            </Space>
          );
        },
        filter: {
          type: 'select',
          placeholder: 'All Users',
          options: filterOptions.users.map((userId) => ({
            label: userId,
            value: userId,
          })),
        },
      },
      {
        title: 'Resource',
        dataIndex: 'resourceType',
        key: 'resourceType',
        width: 150,
        render: (resourceType: string) => (
          <Space size="small">
            {resourceTypeIcons[resourceType] || <FileSearchOutlined />}
            <span>{resourceType}</span>
          </Space>
        ),
        sorter: true,
        filter: {
          type: 'select',
          placeholder: 'All Resources',
          options: filterOptions.resources.map((resource) => ({
            label: resource,
            value: resource,
          })),
        },
      },
      {
        title: 'Resource ID',
        dataIndex: 'resourceId',
        key: 'resourceId',
        width: 200,
        ellipsis: true,
        render: (resourceId: string) => (
          <ResourceIdText>{resourceId}</ResourceIdText>
        ),
      },
      {
        title: 'Details',
        key: 'details',
        width: 100,
        render: (_: unknown, record: AuditLogRow) => {
          const hasMetadata =
            record.metadata && Object.keys(record.metadata).length > 0;
          const hasChanges =
            record.changes && Object.keys(record.changes).length > 0;

          if (!hasMetadata && !hasChanges) return null;

          return (
            <Popover
              content={
                <PopoverContent>
                  {hasMetadata && (
                    <>
                      <PopoverTitle $isFirst>Metadata</PopoverTitle>
                      {record.metadata?.ipAddress && (
                        <MetadataRow>
                          <MetadataLabel>IP Address:</MetadataLabel>
                          <MetadataValue>
                            {record.metadata.ipAddress}
                          </MetadataValue>
                        </MetadataRow>
                      )}
                      {record.metadata?.userAgent && (
                        <MetadataRow>
                          <MetadataLabel>User Agent:</MetadataLabel>
                          <MetadataValue>
                            {record.metadata.userAgent}
                          </MetadataValue>
                        </MetadataRow>
                      )}
                    </>
                  )}
                  {hasChanges && (
                    <>
                      <PopoverTitle $isFirst={!hasMetadata}>
                        Changes
                      </PopoverTitle>
                      <ChangesPre>
                        {JSON.stringify(record.changes, null, 2)}
                      </ChangesPre>
                    </>
                  )}
                </PopoverContent>
              }
              title="Audit Log Details"
              trigger="click"
            >
              <DetailIcon />
            </Popover>
          );
        },
      },
    ],
    [filterOptions],
  );

  const auditLogRows: AuditLogRow[] = auditLogsResponse?.items || [];

  if (logsError) {
    return <ErrorPage />;
  }

  return (
    <TablePageLayout
      title="Audit Logs"
      description="View all audit logs for your team."
    >
      <DataTable
        columns={columns}
        data={auditLogRows}
        rowKey="auditLogId"
        loading={logsLoading}
        maxVisibleFilters={4}
        pagination={{
          current: auditLogsResponse?.pagination?.page || 1,
          pageSize: auditLogsResponse?.pagination?.pageSize || 10,
          total: auditLogsResponse?.pagination?.totalItems || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        emptyState={{
          icon: <EmptyStateIcon />,
          title: 'No audit logs found',
          description:
            'Audit logs will appear here as actions are performed in your team.',
          overrideOnFiltersPresent: {
            title: 'No audit logs match your filters',
            description: 'Try adjusting your filters to see more results.',
          },
        }}
      />
    </TablePageLayout>
  );
};

export default AuditLogsPage;
